import React, { useState, Fragment } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Plus, Package, DollarSign, Globe, ChevronRight, AlertCircle, Sparkles } from 'lucide-react';
interface CreateProductModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (data: any) => void;
}
export function CreateProductModal({
  isOpen,
  onClose,
  onCreate
}: CreateProductModalProps) {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    name: '',
    sku: '',
    ean: '',
    category: '',
    price: '',
    minPrice: '',
    marketplaces: [{
      name: 'Site Próprio',
      enabled: true
    }, {
      name: 'Amazon',
      enabled: false
    }, {
      name: 'Mercado Livre',
      enabled: false
    }, {
      name: 'Magazine Luiza',
      enabled: false
    }],
    competitorUrl: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showSuccess, setShowSuccess] = useState(false);
  if (!isOpen) return null;
  const updateField = (field: string, value: any) => {
    setForm(prev => ({
      ...prev,
      [field]: value
    }));
    if (errors[field]) {
      setErrors(prev => {
        const next = {
          ...prev
        };
        delete next[field];
        return next;
      });
    }
  };
  const generateSku = () => {
    if (form.name) {
      const nameWords = form.name.split(' ').filter(w => w.length > 2);
      const nameCode = nameWords.slice(0, 3).map(w => w.substring(0, 3).toUpperCase()).join('-');
      updateField('sku', nameCode);
    }
  };
  const validateStep = (s: number): boolean => {
    const newErrors: Record<string, string> = {};
    if (s === 0) {
      if (!form.name.trim()) newErrors.name = 'Nome é obrigatório';
      if (!form.sku.trim()) newErrors.sku = 'SKU é obrigatório';
      if (!form.ean.trim()) newErrors.ean = 'EAN é obrigatório';else if (!/^\d{8,14}$/.test(form.ean.replace(/\s/g, ''))) newErrors.ean = 'EAN deve ter entre 8 e 14 dígitos';
      if (!form.category) newErrors.category = 'Selecione uma categoria';
    }
    if (s === 1) {
      if (!form.price || parseFloat(form.price) <= 0) newErrors.price = 'Informe um preço válido';
      if (form.minPrice && parseFloat(form.minPrice) >= parseFloat(form.price)) {
        newErrors.minPrice = 'Preço mínimo deve ser menor que o preço atual';
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleNext = () => {
    if (validateStep(step)) {
      setStep(prev => Math.min(prev + 1, 2));
    }
  };
  const handleBack = () => setStep(prev => Math.max(prev - 1, 0));
  const handleCreate = () => {
    if (validateStep(1)) {
      setShowSuccess(true);
      setTimeout(() => {
        onCreate(form);
        setShowSuccess(false);
        setStep(0);
        setForm({
          name: '',
          sku: '',
          ean: '',
          category: '',
          price: '',
          minPrice: '',
          competitorUrl: '',
          marketplaces: [{
            name: 'Site Próprio',
            enabled: true
          }, {
            name: 'Amazon',
            enabled: false
          }, {
            name: 'Mercado Livre',
            enabled: false
          }, {
            name: 'Magazine Luiza',
            enabled: false
          }]
        });
        onClose();
      }, 1200);
    }
  };
  const steps = [{
    label: 'Informações',
    icon: Package
  }, {
    label: 'Precificação',
    icon: DollarSign
  }, {
    label: 'Canais',
    icon: Globe
  }];
  const toggleMarketplace = (index: number) => {
    const updated = [...form.marketplaces];
    updated[index] = {
      ...updated[index],
      enabled: !updated[index].enabled
    };
    updateField('marketplaces', updated);
  };
  return <AnimatePresence data-id="element-56">
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-57">
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" data-id="element-58" />

        <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 300
      }} className="relative bg-white rounded-2xl shadow-2xl w-full max-w-xl overflow-hidden" data-id="element-59">
          {/* Success overlay */}
          <AnimatePresence data-id="element-60">
            {showSuccess && <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} className="absolute inset-0 z-10 bg-white flex flex-col items-center justify-center" data-id="element-61">
                <motion.div initial={{
              scale: 0
            }} animate={{
              scale: 1
            }} transition={{
              type: 'spring',
              damping: 15
            }} className="h-16 w-16 rounded-full bg-emerald-100 flex items-center justify-center mb-4" data-id="element-62">
                  <Sparkles className="h-8 w-8 text-emerald-600" data-id="element-63" />
                </motion.div>
                <p className="text-lg font-bold text-navy-900" data-id="element-64">
                  Produto Criado!
                </p>
                <p className="text-sm text-slate-500 mt-1" data-id="element-65">
                  O monitoramento será iniciado automaticamente.
                </p>
              </motion.div>}
          </AnimatePresence>

          {/* Header */}
          <div className="px-6 py-5 border-b border-slate-100" data-id="element-66">
            <div className="flex items-center justify-between mb-4" data-id="element-67">
              <h2 className="text-lg font-bold text-navy-900" data-id="element-68">Novo Produto</h2>
              <button onClick={onClose} className="p-1 text-slate-400 hover:text-navy-900 transition-colors rounded-lg hover:bg-slate-100" data-id="element-69">
                <X className="h-5 w-5" data-id="element-70" />
              </button>
            </div>

            {/* Step indicator */}
            <div className="flex items-center gap-1" data-id="element-71">
              {steps.map((s, i) => {
              const Icon = s.icon;
              const isActive = i === step;
              const isComplete = i < step;
              return <Fragment key={i} data-id="element-72">
                    <button onClick={() => {
                  if (isComplete) setStep(i);
                }} className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${isActive ? 'bg-navy-900 text-white' : isComplete ? 'bg-emerald-100 text-emerald-700 cursor-pointer' : 'bg-slate-100 text-slate-400'}`} data-id="element-73">
                      <Icon className="h-3.5 w-3.5" data-id="element-74" />
                      {s.label}
                    </button>
                    {i < steps.length - 1 && <ChevronRight className={`h-3 w-3 ${i < step ? 'text-emerald-400' : 'text-slate-300'}`} data-id="element-75" />}
                  </Fragment>;
            })}
            </div>
          </div>

          {/* Form content */}
          <div className="p-6 min-h-[320px]" data-id="element-76">
            <AnimatePresence mode="wait" data-id="element-77">
              {/* Step 0: Basic Info */}
              {step === 0 && <motion.div key="step-0" initial={{
              opacity: 0,
              x: 20
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -20
            }} transition={{
              duration: 0.2
            }} className="space-y-4" data-id="element-78">
                  <div className="space-y-1.5" data-id="element-79">
                    <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider" data-id="element-80">
                      Nome do Produto <span className="text-red-400" data-id="element-81">*</span>
                    </label>
                    <input type="text" value={form.name} onChange={e => updateField('name', e.target.value)} placeholder='Ex: Smart TV Samsung 55" 4K' className={`w-full px-3 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-900 transition-colors ${errors.name ? 'border-red-300 bg-red-50' : 'border-slate-200'}`} data-id="element-82" />
                    {errors.name && <p className="text-xs text-red-500 flex items-center gap-1" data-id="element-83">
                        <AlertCircle className="h-3 w-3" data-id="element-84" />
                        {errors.name}
                      </p>}
                  </div>

                  <div className="space-y-1.5" data-id="element-85">
                    <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider" data-id="element-86">
                      EAN / Código de Barras{' '}
                      <span className="text-red-400" data-id="element-87">*</span>
                    </label>
                    <input type="text" value={form.ean} onChange={e => updateField('ean', e.target.value.replace(/\D/g, '').substring(0, 14))} placeholder="Ex: 7891234567890" maxLength={14} className={`w-full px-3 py-2.5 border rounded-lg text-sm font-mono tracking-wider focus:outline-none focus:ring-2 focus:ring-navy-900 transition-colors ${errors.ean ? 'border-red-300 bg-red-50' : 'border-slate-200'}`} data-id="element-88" />
                    {errors.ean ? <p className="text-xs text-red-500 flex items-center gap-1" data-id="element-89">
                        <AlertCircle className="h-3 w-3" data-id="element-90" />
                        {errors.ean}
                      </p> : <p className="text-[10px] text-slate-400" data-id="element-91">
                        Código EAN-8, EAN-13 ou GTIN-14 do produto
                      </p>}
                  </div>

                  <div className="grid grid-cols-2 gap-4" data-id="element-92">
                    <div className="space-y-1.5" data-id="element-93">
                      <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider" data-id="element-94">
                        SKU <span className="text-red-400" data-id="element-95">*</span>
                      </label>
                      <div className="flex gap-2" data-id="element-96">
                        <input type="text" value={form.sku} onChange={e => updateField('sku', e.target.value.toUpperCase())} placeholder="SAM-TV55-4K" className={`flex-1 px-3 py-2.5 border rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-navy-900 ${errors.sku ? 'border-red-300 bg-red-50' : 'border-slate-200'}`} data-id="element-97" />
                        <button type="button" onClick={generateSku} className="px-2.5 py-2 bg-slate-100 border border-slate-200 rounded-lg text-xs font-medium text-slate-600 hover:bg-slate-200 transition-colors whitespace-nowrap" title="Gerar SKU automaticamente" data-id="element-98">
                          Auto
                        </button>
                      </div>
                      {errors.sku && <p className="text-xs text-red-500 flex items-center gap-1" data-id="element-99">
                          <AlertCircle className="h-3 w-3" data-id="element-100" />
                          {errors.sku}
                        </p>}
                    </div>

                    <div className="space-y-1.5" data-id="element-101">
                      <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider" data-id="element-102">
                        Categoria <span className="text-red-400" data-id="element-103">*</span>
                      </label>
                      <select value={form.category} onChange={e => updateField('category', e.target.value)} className={`w-full px-3 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-900 bg-white ${errors.category ? 'border-red-300 bg-red-50' : 'border-slate-200'} ${!form.category ? 'text-slate-400' : 'text-navy-900'}`} data-id="element-104">
                        <option value="" data-id="element-105">Selecionar...</option>
                        <option data-id="element-106">Eletrônicos</option>
                        <option data-id="element-107">Eletrodomésticos</option>
                        <option data-id="element-108">Informática</option>
                        <option data-id="element-109">Games</option>
                        <option data-id="element-110">Eletroportáteis</option>
                        <option data-id="element-111">Áudio</option>
                      </select>
                      {errors.category && <p className="text-xs text-red-500 flex items-center gap-1" data-id="element-112">
                          <AlertCircle className="h-3 w-3" data-id="element-113" />
                          {errors.category}
                        </p>}
                    </div>
                  </div>
                </motion.div>}

              {/* Step 1: Pricing */}
              {step === 1 && <motion.div key="step-1" initial={{
              opacity: 0,
              x: 20
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -20
            }} transition={{
              duration: 0.2
            }} className="space-y-5" data-id="element-114">
                  <div className="bg-blue-50 border border-blue-100 rounded-lg p-3" data-id="element-115">
                    <p className="text-xs text-blue-700" data-id="element-116">
                      <span className="font-semibold" data-id="element-117">Dica:</span> Defina o
                      preço mínimo para receber alertas quando o mercado cair
                      abaixo desse valor.
                    </p>
                  </div>

                  <div className="space-y-1.5" data-id="element-118">
                    <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider" data-id="element-119">
                      Seu Preço Atual (R$){' '}
                      <span className="text-red-400" data-id="element-120">*</span>
                    </label>
                    <div className="relative" data-id="element-121">
                      <span className="absolute left-3 top-2.5 text-slate-400 text-sm font-medium" data-id="element-122">
                        R$
                      </span>
                      <input type="number" value={form.price} onChange={e => updateField('price', e.target.value)} placeholder="0,00" step="0.01" className={`w-full pl-10 pr-4 py-2.5 border rounded-lg text-sm font-bold text-navy-900 focus:outline-none focus:ring-2 focus:ring-navy-900 ${errors.price ? 'border-red-300 bg-red-50' : 'border-slate-200'}`} data-id="element-123" />
                    </div>
                    {errors.price && <p className="text-xs text-red-500 flex items-center gap-1" data-id="element-124">
                        <AlertCircle className="h-3 w-3" data-id="element-125" />
                        {errors.price}
                      </p>}
                  </div>

                  <div className="space-y-1.5" data-id="element-126">
                    <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider" data-id="element-127">
                      Preço Mínimo (R$)
                    </label>
                    <div className="relative" data-id="element-128">
                      <span className="absolute left-3 top-2.5 text-slate-400 text-sm font-medium" data-id="element-129">
                        R$
                      </span>
                      <input type="number" value={form.minPrice} onChange={e => updateField('minPrice', e.target.value)} placeholder="0,00" step="0.01" className={`w-full pl-10 pr-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-900 ${errors.minPrice ? 'border-red-300 bg-red-50' : 'border-slate-200'}`} data-id="element-130" />
                    </div>
                    {errors.minPrice && <p className="text-xs text-red-500 flex items-center gap-1" data-id="element-131">
                        <AlertCircle className="h-3 w-3" data-id="element-132" />
                        {errors.minPrice}
                      </p>}
                    <p className="text-[10px] text-slate-400" data-id="element-133">
                      Opcional — alerta quando o preço cair abaixo deste valor
                    </p>
                  </div>

                  {/* Quick margin preview */}
                  {form.price && form.minPrice && <motion.div initial={{
                opacity: 0,
                height: 0
              }} animate={{
                opacity: 1,
                height: 'auto'
              }} className="bg-slate-50 rounded-lg p-3 border border-slate-100" data-id="element-134">
                      <div className="flex justify-between text-xs" data-id="element-135">
                        <span className="text-slate-500" data-id="element-136">
                          Margem de proteção
                        </span>
                        <span className="font-bold text-navy-900" data-id="element-137">
                          {((1 - parseFloat(form.minPrice) / parseFloat(form.price)) * 100).toFixed(1)}
                          %
                        </span>
                      </div>
                      <div className="mt-2 h-1.5 bg-slate-200 rounded-full overflow-hidden" data-id="element-138">
                        <div className="h-full bg-emerald-500 rounded-full transition-all duration-300" style={{
                    width: `${Math.min(100, Math.max(0, (1 - parseFloat(form.minPrice) / parseFloat(form.price)) * 100 * 3))}%`
                  }} data-id="element-139" />
                      </div>
                    </motion.div>}
                </motion.div>}

              {/* Step 2: Channels & Links */}
              {step === 2 && <motion.div key="step-2" initial={{
              opacity: 0,
              x: 20
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -20
            }} transition={{
              duration: 0.2
            }} className="space-y-5" data-id="element-140">
                  <div data-id="element-141">
                    <h3 className="text-sm font-semibold text-navy-900 mb-3" data-id="element-142">
                      Marketplaces Ativos
                    </h3>
                    <p className="text-xs text-slate-400 mb-4" data-id="element-143">
                      Selecione onde este produto será vendido. Preços por canal
                      podem ser ajustados depois.
                    </p>
                    <div className="grid grid-cols-2 gap-3" data-id="element-144">
                      {form.marketplaces.map((mp, i) => <button key={mp.name} type="button" onClick={() => toggleMarketplace(i)} className={`flex items-center gap-3 p-3 rounded-lg border text-left transition-all ${mp.enabled ? 'border-navy-200 bg-navy-50 ring-1 ring-navy-200' : 'border-slate-200 bg-white hover:border-slate-300'}`} data-id="element-145">
                          <div className={`h-8 w-8 rounded-lg flex items-center justify-center text-xs font-bold ${mp.enabled ? 'bg-navy-900 text-white' : 'bg-slate-100 text-slate-400'}`} data-id="element-146">
                            {mp.name.charAt(0)}
                          </div>
                          <div data-id="element-147">
                            <p className={`text-sm font-medium ${mp.enabled ? 'text-navy-900' : 'text-slate-500'}`} data-id="element-148">
                              {mp.name}
                            </p>
                            <p className="text-[10px] text-slate-400" data-id="element-149">
                              {mp.enabled ? 'Ativo' : 'Inativo'}
                            </p>
                          </div>
                        </button>)}
                    </div>
                  </div>

                  <div className="border-t border-slate-100 pt-5" data-id="element-150">
                    <h3 className="text-sm font-semibold text-navy-900 mb-3" data-id="element-151">
                      Link de Concorrente (Opcional)
                    </h3>
                    <p className="text-xs text-slate-400 mb-3" data-id="element-152">
                      Adicione uma URL para iniciar o monitoramento de preço.
                      Mais links podem ser adicionados depois.
                    </p>
                    <div className="relative" data-id="element-153">
                      <Globe className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" data-id="element-154" />
                      <input type="url" value={form.competitorUrl} onChange={e => updateField('competitorUrl', e.target.value)} placeholder="https://amazon.com.br/dp/..." className="w-full pl-9 pr-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-900" data-id="element-155" />
                    </div>
                  </div>
                </motion.div>}
            </AnimatePresence>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex items-center justify-between" data-id="element-156">
            <div data-id="element-157">
              {step > 0 && <button onClick={handleBack} className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-navy-900 transition-colors" data-id="element-158">
                  Voltar
                </button>}
            </div>
            <div className="flex gap-3" data-id="element-159">
              <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-500 hover:text-slate-700 transition-colors" data-id="element-160">
                Cancelar
              </button>
              {step < 2 ? <button onClick={handleNext} className="px-5 py-2 bg-navy-900 text-white rounded-lg text-sm font-medium hover:bg-navy-800 transition-colors flex items-center shadow-sm" data-id="element-161">
                  Próximo
                  <ChevronRight className="h-4 w-4 ml-1" data-id="element-162" />
                </button> : <button onClick={handleCreate} className="px-5 py-2 bg-navy-900 text-white rounded-lg text-sm font-medium hover:bg-navy-800 transition-colors flex items-center shadow-sm" data-id="element-163">
                  <Plus className="h-4 w-4 mr-1.5" data-id="element-164" />
                  Criar Produto
                </button>}
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>;
}